/* eslint-disable max-len */
/* eslint-disable object-curly-newline */
import { Signal } from '@angular/core';
import { Store } from '@ngrx/store';

import { InitialConfigDefault, Position, ProductViewConfig } from '../models/product-view-config.model';
import { GridProductStyle, ProductViewTemplateModel } from '../models/product-view-template.model';

import { GridElemType } from '@core/domain/types/product.types';
import { getCmsConfigValue, getIsOpenForSale, getRedirectToWorldWide } from '@core/infrastructure/state/core.selectors';
import { CategoryPublicModels } from '@features/category';
import { MocacoAttributesPublicConstants } from '@features/products/mocaco-attributes';
import { ProductPublicModels } from '@features/products/product';
import { Product } from '@features/products/product/domain/models/product.models';
import { PublicCartProductTypes } from '@features/shopping-cart';
import { Resolutions } from '@layout/domain/models/layout.models';
import { getResolution } from '@layout/infrastructure/state/layout.selectors';
import { CategoryConfiguration, HiddenFieldType } from '@shared/product/models';

export function getShowAddToCart(
    store: Store,
    product: ProductPublicModels.PublicProduct,
    category: CategoryPublicModels.PublicMenuCategory | CategoryPublicModels.PublicCategory,
    config: any,
): boolean {
    const enableAddToCart = store.selectSignal(getCmsConfigValue('enable_shop_from_PLP'))() ?? true;
    const isPdp = config.place === 'PRODUCT_DETAIL';
    // TODO ECOMDUTI-25479 Esto se tiene que hacer en el padre a nivel de grid, se ha puesto aqui por las limitaciones de studio
    let enableAddToCartAttachment = category?.attachments?.find((attachment) => attachment.name === 'hide_shop_from_PLP') ?? false;
    const customizableAttachment = product?.attributes?.find((attribute) =>
        attribute.name === MocacoAttributesPublicConstants.MocacoAttributesEnum.XCUSTOMIZABLE) ?? false;
    enableAddToCartAttachment = isPdp ? false : enableAddToCartAttachment;
    if (enableAddToCart && !enableAddToCartAttachment && !customizableAttachment) {
        return (store.selectSignal(getIsOpenForSale)() || !!store.selectSignal(getRedirectToWorldWide)())
        && product.gridElemType !== GridElemType.BANNER
        && product.gridElemType !== GridElemType.BUNDLE;
    }

    return false;
}

export function extendDefined<T>(target: T, source: Partial<T> | null | undefined): T {
    if (!source) {
        return target;
    }

    const result: T = { ...target };
    for (const key of Object.keys(source)) {
        const typedKey = key as keyof T;
        if (source[typedKey] !== undefined && source[typedKey] !== null) {
            result[typedKey] = source[typedKey];
        }
    }
    return result;
}

export function getBannerConfig(product: ProductPublicModels.PublicProduct, idMedia: string): {
    isBannerNoClickable: boolean;
    isProductBannerStudio: boolean;
    isCategoryBanner: boolean;
    bannerProductFinded: boolean;
} {
    const bannerProductFinded = product.attributes.some(attribute => attribute.value === 'BANNER_PRODUCT');
    const isBannerStudio = product.gridElemType === GridElemType.BANNER;
    const bannerLinkFinded = product.attributes.some(attribute => attribute.name.includes('BANNERLINK'));
    const bannerLinkStudio = product.bannerLinks?.some(b => b.datatype === 'categoryLink' && b.idMedia === idMedia);
    const bannerProductLinkStudio = product.bannerLinks?.some(b => b.datatype === 'productLink' && b.idMedia === idMedia);

    const isBannerNoClickable = bannerProductFinded || isBannerStudio && !bannerLinkStudio && !bannerProductLinkStudio;
    const isProductBannerStudio = isBannerStudio && bannerProductLinkStudio;
    const isCategoryBanner = bannerLinkFinded || isBannerStudio && !!bannerLinkStudio;

    return {
        isBannerNoClickable, isProductBannerStudio, isCategoryBanner, bannerProductFinded,
    };
}

export function setNewConfig(
    config: ProductViewConfig,
    product: ProductPublicModels.PublicProduct,
    isBannerNoClickable: boolean,
    isProductBannerStudio: boolean,
    isCategoryBanner: boolean,
    bannerProductFinded: boolean,
    urlLinkAttr: ProductPublicModels.PublicProductAttribute,
    categoryConf: { hiddenField: HiddenFieldType; style: GridProductStyle },
    showSoldOut: boolean,
    hasVideo: boolean,
): ProductViewTemplateModel {
    const newConfig: ProductViewTemplateModel = {
        ...InitialConfigDefault,
        ...extendDefined({}, config), // Only include defined properties from config
        hasVideo,
        showSoldOut,
        bannerProductFinded: bannerProductFinded,
    };

    if (config.textPosition === Position.none) {
        newConfig.showName = false;
        newConfig.showColors = false;
    }

    if (isBannerNoClickable || urlLinkAttr && !urlLinkAttr.value.startsWith('/') && !urlLinkAttr.value.startsWith('http') && !urlLinkAttr.value.startsWith('www')) {
        newConfig.clickable = false;
    }

    if (isBannerNoClickable || isProductBannerStudio || isCategoryBanner || urlLinkAttr) {
        newConfig.showDescription = false;
        newConfig.showTags = false;
        newConfig.showQuickly = false;
        newConfig.showColors = false;
        newConfig.showDiscount = false;
        newConfig.showMadeInExclusive = true;
    }

    if (isBannerNoClickable || isProductBannerStudio || isCategoryBanner || product.productType === PublicCartProductTypes.PHYSICAL_CARD || product.productType === PublicCartProductTypes.VIRTUAL_CARD) {
        newConfig.showDetails = false;
    }

    if (urlLinkAttr) {
        newConfig.textPosition = Position.bottom;
        newConfig.showName = true;
        newConfig.showPrice = true;
    }

    if (newConfig.showSoldOut) {
        newConfig.clickable = false;
    }

    switch (categoryConf?.hiddenField) {
        case HiddenFieldType.ALL:
            newConfig.showDetails = false;
            break;

        case HiddenFieldType.DESCRIPTION:
            newConfig.showName = false;
            newConfig.showColors = false;
            break;

        case HiddenFieldType.PRICE:
            newConfig.showPrice = false;
            break;
    }

    return newConfig;
}

export function getTemplateConfigView(
    product: ProductPublicModels.PublicProduct,
    config: ProductViewConfig,
    categoryConfig: CategoryConfiguration, // TODO ECOMDUTI-25479 La config de categorias para configurar la vista debe pasarse a los grids
    store: Store,
    hasVideo: boolean,
    imagePriority: boolean,
    idMedia: string
): ProductViewTemplateModel {
    const { isBannerNoClickable, isProductBannerStudio, isCategoryBanner, bannerProductFinded } = getBannerConfig(product, idMedia);
    const showSoldOut = categoryConfig?.soldOut && product.status.selectedColor.sizes && !product.status.selectedColor.sizes.find(size => size.visibilityValue !== 'BACK_SOON');
    const categoryConf = categoryConfig?.productConf?.get(+product.id);
    const urlLinkAttr = product.attributes.find(attribute => attribute.name.includes('URLLINK'));
    const newConfig = setNewConfig(config, product, isBannerNoClickable, isProductBannerStudio, isCategoryBanner, bannerProductFinded, urlLinkAttr, categoryConf, showSoldOut, hasVideo);
    const { showQuickly, showQuicklyMobile } = getConfigAddToCartView(store, product, newConfig, categoryConfig?.category);

    return {
        ...newConfig,
        showViewBundleProducts: config.showViewBundleProducts,
        showQuickly,
        showQuicklyMobile,
        media: {
            widthVW: newConfig.media.widthVW ?? '(max-width: 800) 100vw, 20vw',
            alt: product?.name ?? 'product-grid-image',
            idMedia,
            imagePriority,
            ratio: newConfig.media.ratio ?? '3-4',
            showWaterMark: config.media?.showWaterMark ?? InitialConfigDefault.media.showWaterMark,
        },
    };
}

export function getConfigAddToCartView(
    store: Store,
    product: Product,
    newConfig: ProductViewTemplateModel,
    category: CategoryPublicModels.PublicMenuCategory | CategoryPublicModels.PublicCategory
): { showQuickly: boolean; showQuicklyMobile: boolean } {
    const showAddToCart: boolean = getShowAddToCart(store, product, category, newConfig);
    const resolution$$: Signal<Resolutions> = store.selectSignal(getResolution);
    const isMobile: boolean = [Resolutions.MOBILE, Resolutions.TABLET].includes(resolution$$()) ?? true;
    if (!showAddToCart) {
        return { showQuickly: false, showQuicklyMobile: false };
    }

    const iconMoreAddCart = newConfig.showQuicklyMobile && isMobile;
    const hoverAddCart = newConfig.showQuickly && !isMobile;
    return {
        showQuicklyMobile: iconMoreAddCart,
        showQuickly: hoverAddCart && !iconMoreAddCart,
    };
}
